/**
 * `/sites/$siteId/content/$contentItemId` — ContentVersion lifecycle
 * editor (issue #173).
 *
 * The standalone analog of `PageDetailPage` for a single ContentItem.
 * The operator can:
 *
 *   - See item metadata (name + type + blockType + timestamps).
 *   - Edit the DRAFT version's `contentJson` in a monospace JSON
 *     textarea, save / publish.
 *   - Unpublish the currently-PUBLISHED version, or amend it via
 *     "Create new draft from this".
 *   - Roll back to any ARCHIVED version, or preview it in isolation
 *     via `/preview/content-version/:id`.
 *
 * Slot order matches PR #203's PageDetailPage: **Published → Draft →
 * Archived**. The published slot carries the "Create new draft from
 * this" CTA — leading with it makes the recovery / amendment path
 * obvious instead of dropping the operator into an editor with no
 * context.
 *
 * 409 VERSION_RACE handling is identical to PageDetailPage: the
 * publish mutation surfaces the typed error code; we render a yellow
 * "Another publish raced you — refreshing" toast and explicitly
 * refetch.
 *
 * MUI only — no Tailwind / CSS modules / raw CSS. The JSON textarea
 * uses a `TextField multiline` with an explicit monospace `sx`, same
 * affordance the page editor exposes.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link, useParams } from "@tanstack/react-router";
import type {
  ContentItemResponse,
  ContentItemWithVersionsResponse,
  ContentVersionResponse,
} from "@ottopilot/shared";
import { useSite } from "../hooks/useSites";
import { useDomains } from "../hooks/useDomains";
import { buildPublicUrl, findPrimaryDomain } from "../lib/publicUrl";
import {
  useArchiveExperimentContentVersion,
  useContentItem,
  useCreateContentVersion,
  useDraftFromContentVersion,
  usePromoteContentVersion,
  usePublishContentVersion,
  useUnpublishContentVersion,
  useUpdateContentVersion,
} from "../hooks/useContentItems";
import { useCreatePreviewToken } from "../hooks/usePreviewToken";
import { useRunJudges } from "../hooks/useRunJudges";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { useToast } from "../components/ToastProvider";
import type { ApiClientError } from "../lib/api";
import { JudgeScoresStrip } from "../features/content/JudgeScoresStrip";
import { ExperimentBacklinks } from "../features/experiments/ExperimentBacklinks";

/** Minimal placeholder contentJson used to seed a brand-new draft. */
const EMPTY_CONTENT_JSON_TEXT = JSON.stringify({ schemaVersion: 1 }, null, 2);

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

type JsonParseResult = { ok: true; value: unknown } | { ok: false; error: string };

function tryParseJson(text: string): JsonParseResult {
  if (text.trim().length === 0) return { ok: false, error: "JSON is empty." };
  try {
    return { ok: true, value: JSON.parse(text) as unknown };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Invalid JSON." };
  }
}

function statusChipColor(
  status: ContentVersionResponse["status"],
): "default" | "primary" | "success" | "warning" {
  switch (status) {
    case "DRAFT":
      return "primary";
    case "PUBLISHED":
      return "success";
    case "EXPERIMENT":
      // Issue #243: distinct from PUBLISHED so the operator can see at a
      // glance which versions are running in experiments only.
      return "warning";
    case "ARCHIVED":
      return "default";
    default:
      return "default";
  }
}

/**
 * Build the `/preview/content-version/:id` URL on the site's primary
 * domain. Returns `null` when no primary domain exists — the caller
 * hides the Preview affordance in that case rather than rendering a
 * broken link.
 *
 * Issue #208: the actual URL the SPA opens is built per-click so the
 * `?t=<jwt>` query param can be appended after the token mutation
 * resolves; this helper only computes the static prefix (used as the
 * presence check for "do we have a hostname at all").
 */
function buildPreviewUrl(primaryHostname: string | undefined, versionId: string): string | null {
  if (!primaryHostname) return null;
  return buildPublicUrl(primaryHostname, `/preview/content-version/${versionId}`);
}

/**
 * Hook + handler factory for the per-row Preview button (issue #208).
 * Returns a callback that mints a preview token, then opens the
 * resulting `?t=...` URL in a new tab. The mutation's `isPending`
 * surface lets the caller disable the button while the token is being
 * minted.
 */
function useContentVersionPreviewHandler(primaryHostname: string | undefined): {
  isPending: boolean;
  openPreview: (versionId: string) => void;
} {
  const createPreviewToken = useCreatePreviewToken();
  const toast = useToast();
  const openPreview = (versionId: string) => {
    if (!primaryHostname) return;
    createPreviewToken.mutate(
      { resourceType: "content-version", resourceId: versionId },
      {
        onSuccess: ({ token }) => {
          const url = buildPublicUrl(
            primaryHostname,
            `/preview/content-version/${versionId}?t=${token}`,
          );
          window.open(url, "_blank", "noopener,noreferrer");
        },
        onError: (err) => toast.showError(err.message || "Could not open preview."),
      },
    );
  };
  return { isPending: createPreviewToken.isPending, openPreview };
}

/**
 * Toast copy for the AI rate-limit + budget-cap surfaces. Centralised
 * here (and mirrored from `AiStudioPage`'s `handleGenerate`) so every
 * call site that fires an AI-gated mutation shows the same operator
 * message — keeps the SPA's voice consistent across surfaces and gives
 * QA one place to update when the copy evolves.
 */
const AI_BUDGET_EXCEEDED_TOAST_MSG =
  "Monthly AI budget exceeded. Use the override banner to continue.";
const AI_RATE_LIMITED_TOAST_MSG = "AI request rate limit hit. Wait a moment and try again.";

/**
 * Small refresh-icon button surfaced beside each `JudgeScoresStrip`.
 * Fires the re-run mutation against a single ContentVersion; on success
 * the parent ContentItem detail query is invalidated and the strip
 * re-renders with the new DTOs (no manual refetch needed).
 *
 * Error handling lives here, not in the hook, because the toast surface
 * is per-button (each slot owns its own user-facing failure message).
 * 429 `AI_BUDGET_EXCEEDED` and 429 `AI_RATE_LIMITED` get distinct copy
 * mirroring the AI Studio generate flow; everything else falls back to
 * the error envelope's `message` (or a default).
 *
 * `testIdSuffix` lets the caller scope the data-testid per slot variant
 * (`-published`, `-draft`, `-archived-<n>`) so QA can target a specific
 * slot's button without ambiguity when multiple coexist on the page.
 */
function RerunJudgesButton({
  contentItemId,
  contentVersionId,
  testIdSuffix,
}: {
  contentItemId: string;
  contentVersionId: string;
  testIdSuffix: string;
}): ReactElement {
  const toast = useToast();
  const runJudges = useRunJudges(contentItemId);

  const handleClick = () => {
    runJudges.mutate(
      { contentVersionId },
      {
        onSuccess: () => toast.showSuccess("Judges re-ran. Scores updated."),
        onError: (err: ApiClientError) => {
          if (err.code === "AI_BUDGET_EXCEEDED") {
            toast.showError(AI_BUDGET_EXCEEDED_TOAST_MSG);
            return;
          }
          if (err.code === "AI_RATE_LIMITED") {
            toast.showError(AI_RATE_LIMITED_TOAST_MSG);
            return;
          }
          toast.showError(err.message || "Could not re-run judges.");
        },
      },
    );
  };

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={
        runJudges.isPending ? (
          <CircularProgress size={14} color="inherit" />
        ) : (
          <RefreshIcon fontSize="small" />
        )
      }
      onClick={handleClick}
      disabled={runJudges.isPending}
      data-testid={`content-version-rerun-judges-button-${testIdSuffix}`}
    >
      {runJudges.isPending ? "Re-running…" : "Re-run judges"}
    </Button>
  );
}

/**
 * Published slot — read-only metadata + Unpublish + Create-new-draft +
 * Preview actions. Top slot per PR #203's ordering.
 */
function PublishedSlot({
  contentItemId,
  published,
  primaryHostname,
  onDraftCreated,
}: {
  contentItemId: string;
  published: ContentVersionResponse;
  primaryHostname: string | undefined;
  onDraftCreated: () => void;
}): ReactElement {
  const toast = useToast();
  const unpublish = useUnpublishContentVersion(contentItemId);
  const draftFrom = useDraftFromContentVersion(contentItemId);
  const preview = useContentVersionPreviewHandler(primaryHostname);
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();

  const handleUnpublish = async () => {
    const ok = await confirm({
      title: "Unpublish this version?",
      body: "Unpublish this version? Pages referencing this block will lose its content.",
      confirmLabel: "Unpublish",
    });
    if (!ok) return;
    unpublish.mutate(
      { versionId: published.id },
      {
        onSuccess: () => toast.showSuccess("Version unpublished."),
        onError: (err) => toast.showError(err.message || "Could not unpublish."),
      },
    );
  };

  const handleDraftFrom = () => {
    draftFrom.mutate(
      { sourceVersionId: published.id },
      {
        onSuccess: () => {
          toast.showSuccess("New draft created from the published version.");
          onDraftCreated();
        },
        onError: (err) => toast.showError(err.message || "Could not create draft."),
      },
    );
  };

  const previewUrl = buildPreviewUrl(primaryHostname, published.id);

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 3 } }}
      data-testid="content-item-detail-published-slot"
    >
      {confirmDialog}
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h3">Currently published</Typography>
            <Chip
              size="small"
              color={statusChipColor(published.status)}
              label={published.status}
              variant="outlined"
              data-testid="content-item-detail-published-status-chip"
            />
            <Typography variant="body2" color="text.secondary">
              v{published.versionNumber}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Published {formatDateTime(published.publishedAt)}
          </Typography>
        </Stack>

        {/* Issue #217: AI judge strip below version metadata, above
            action buttons. Renders nothing when no judge run has
            landed (manual / pre-AI versions). The Re-run judges
            button sits beside the strip so the operator can refresh
            scores in place without leaving the slot. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
        >
          <JudgeScoresStrip judges={published.judges} />
          <RerunJudgesButton
            contentItemId={contentItemId}
            contentVersionId={published.id}
            testIdSuffix="published"
          />
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          {previewUrl ? (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => preview.openPreview(published.id)}
              disabled={preview.isPending}
              data-testid="content-item-detail-published-preview-button"
            >
              Preview
            </Button>
          ) : null}
          <Button
            color="warning"
            onClick={() => {
              void handleUnpublish();
            }}
            disabled={unpublish.isPending}
            data-testid="content-item-detail-unpublish-button"
          >
            {unpublish.isPending ? "Unpublishing…" : "Unpublish"}
          </Button>
          <Button
            variant="contained"
            onClick={handleDraftFrom}
            disabled={draftFrom.isPending}
            data-testid="content-item-detail-draft-from-published-button"
          >
            {draftFrom.isPending ? "Creating…" : "Create new draft from this"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Draft slot — editable JSON textarea + Save / Publish + Preview.
 * Mirrors PageDetailPage's DraftSlot but operates on the
 * `contentJson` field of a single ContentVersion.
 */
function DraftSlot({
  contentItemId,
  draft,
  primaryHostname,
  text,
  setText,
  onVersionRace,
}: {
  contentItemId: string;
  draft: ContentVersionResponse;
  primaryHostname: string | undefined;
  text: string;
  setText: (next: string) => void;
  onVersionRace: () => void;
}): ReactElement {
  const toast = useToast();
  const update = useUpdateContentVersion(contentItemId);
  const publish = usePublishContentVersion(contentItemId);
  const preview = useContentVersionPreviewHandler(primaryHostname);

  // Seed the textarea from the server-side draft. Recomputed when the
  // server-side `contentJson` changes (TanStack Query hands a fresh
  // object on every refetch that produced new bytes). Local edits never
  // re-trigger this effect because `seed` is derived from the server
  // payload, not from `text`.
  const seed = useMemo(() => prettyJson(draft.contentJson), [draft.contentJson]);
  const lastSyncedSeed = useRef<string | null>(null);
  useEffect(() => {
    if (lastSyncedSeed.current !== seed) {
      lastSyncedSeed.current = seed;
      setText(seed);
    }
  }, [seed, setText]);

  const parsed = tryParseJson(text);
  const isUnchanged = text === seed;
  const canSave = !isUnchanged && parsed.ok && !update.isPending;
  const canPublish = isUnchanged && parsed.ok && !publish.isPending;

  const handleSave = () => {
    if (!parsed.ok) return;
    update.mutate(
      {
        versionId: draft.id,
        input: { contentJson: parsed.value as never },
      },
      {
        onSuccess: () => {
          toast.showSuccess("Draft saved.");
          setText(prettyJson(parsed.value));
        },
        onError: (err) => {
          toast.showError(err.message || "Could not save draft.");
        },
      },
    );
  };

  // Flag-override confirmation. Server gates publish on unresolved
  // HIGH/CRITICAL flags (per spec §10 Approval Gate) and exposes an
  // `overrideFlags: true` opt-out so the human stays in control: judges
  // are advisory, the operator decides whether to ship. We catch the
  // 422 PUBLISH_BLOCKED_BY_FLAGS, surface the count, and retry on
  // confirm. Server records the override in the activity log + auto-
  // resolves the cleared flags.
  const [overrideDialog, setOverrideDialog] = useState<{ blockingFlagCount: number } | null>(null);

  // Issue #243: publish-mode picker. "primary" (default) flips DRAFT →
  // PUBLISHED and demotes the prior PUBLISHED peer; "experiment" flips
  // DRAFT → EXPERIMENT with no demotion.
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishDialogMode, setPublishDialogMode] = useState<"primary" | "experiment">("primary");

  const runPublish = (mode: "primary" | "experiment", overrideFlags: boolean): void => {
    publish.mutate(
      {
        versionId: draft.id,
        ...(mode === "experiment" ? { mode } : {}),
        ...(overrideFlags ? { overrideFlags: true } : {}),
      },
      {
        onSuccess: () =>
          toast.showSuccess(
            mode === "experiment"
              ? overrideFlags
                ? "Draft published for experiment use. Flags overridden — see activity log."
                : "Draft published for experiment use."
              : overrideFlags
                ? "Draft published. Flags overridden — see activity log."
                : "Draft published.",
          ),
        onError: (err: ApiClientError) => {
          if (err.code === "VERSION_RACE") {
            toast.show({
              message: "Another publish raced you — refreshing.",
              severity: "warning",
            });
            onVersionRace();
            return;
          }
          if (err.code === "PUBLISH_BLOCKED_BY_FLAGS") {
            const details = err.details as { blockingFlagCount?: number } | null | undefined;
            const blockingFlagCount =
              typeof details?.blockingFlagCount === "number" ? details.blockingFlagCount : 0;
            setOverrideDialog({ blockingFlagCount });
            return;
          }
          toast.showError(err.message || "Could not publish draft.");
        },
      },
    );
  };

  const handleOpenPublishDialog = (): void => {
    setPublishDialogMode("primary");
    setPublishDialogOpen(true);
  };

  const handleConfirmPublish = (): void => {
    const mode = publishDialogMode;
    setPublishDialogOpen(false);
    runPublish(mode, false);
  };

  const handleConfirmOverride = (): void => {
    const mode = publishDialogMode;
    setOverrideDialog(null);
    runPublish(mode, true);
  };

  const previewUrl = buildPreviewUrl(primaryHostname, draft.id);

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 3 } }}
      data-testid="content-item-detail-draft-slot"
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h3">Current draft</Typography>
            <Chip
              size="small"
              color={statusChipColor(draft.status)}
              label={draft.status}
              variant="outlined"
              data-testid="content-item-detail-draft-status-chip"
            />
            <Typography variant="body2" color="text.secondary">
              v{draft.versionNumber}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Created {formatDateTime(draft.createdAt)} · Updated {formatDateTime(draft.updatedAt)}
          </Typography>
        </Stack>

        {/* Issue #217: AI judge strip below version metadata, above
            the editor + action buttons. The Re-run judges button sits
            beside the strip so the operator can refresh scores without
            leaving the slot. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
        >
          <JudgeScoresStrip judges={draft.judges} />
          <RerunJudgesButton
            contentItemId={contentItemId}
            contentVersionId={draft.id}
            testIdSuffix="draft"
          />
        </Stack>

        <Box>
          <TextField
            label="Content JSON (contentJson)"
            multiline
            minRows={10}
            maxRows={30}
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            error={!parsed.ok}
            helperText={
              parsed.ok
                ? isUnchanged
                  ? "No unsaved changes."
                  : "Unsaved changes — save before publishing."
                : `Invalid JSON: ${parsed.error}`
            }
            sx={{
              "& textarea": {
                fontFamily:
                  '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Monaco, monospace',
                fontSize: 13,
                lineHeight: 1.5,
              },
            }}
            slotProps={{
              input: {
                spellCheck: false,
                autoCorrect: "off",
                autoCapitalize: "off",
              },
            }}
            data-testid="content-item-detail-draft-json-input"
          />
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          {previewUrl ? (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => preview.openPreview(draft.id)}
              disabled={preview.isPending}
              data-testid="content-item-detail-draft-preview-button"
            >
              Preview
            </Button>
          ) : null}
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="content-item-detail-draft-save-button"
          >
            {update.isPending ? "Saving…" : "Save draft"}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleOpenPublishDialog}
            disabled={!canPublish}
            data-testid="content-item-detail-publish-button"
          >
            {publish.isPending ? "Publishing…" : "Publish"}
          </Button>
        </Stack>
      </Stack>

      {/*
        Issue #243: publish dialog. Mode picker between
          "primary" (DRAFT → PUBLISHED, demote prior) and
          "experiment" (DRAFT → EXPERIMENT, no demote).
      */}
      <Dialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        data-testid="content-item-detail-publish-dialog"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Publish draft</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Pick how this draft should reach traffic. The primary version is what every visitor
              sees by default; experiment versions are only reachable through a running experiment.
            </Typography>
            <FormControl>
              <RadioGroup
                value={publishDialogMode}
                onChange={(e) => setPublishDialogMode(e.target.value as "primary" | "experiment")}
              >
                <FormControlLabel
                  value="primary"
                  control={<Radio data-testid="content-item-detail-publish-mode-primary" />}
                  label={
                    <Stack spacing={0.25}>
                      <Typography variant="body2">Make this the primary</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Replaces the currently-live version. The previous primary is archived.
                      </Typography>
                    </Stack>
                  }
                />
                <FormControlLabel
                  value="experiment"
                  control={<Radio data-testid="content-item-detail-publish-mode-experiment" />}
                  label={
                    <Stack spacing={0.25}>
                      <Typography variant="body2">Publish for experiment use</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Joins the experiment slot, doesn&apos;t change the live block.
                      </Typography>
                    </Stack>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPublishDialogOpen(false)}
            data-testid="content-item-detail-publish-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmPublish}
            data-testid="content-item-detail-publish-dialog-confirm"
          >
            {publishDialogMode === "experiment" ? "Publish for experiment" : "Make primary"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={overrideDialog !== null}
        onClose={() => setOverrideDialog(null)}
        data-testid="content-item-detail-publish-override-dialog"
      >
        <DialogTitle>Publish despite quality flags?</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant="body2">
              {overrideDialog && overrideDialog.blockingFlagCount > 0
                ? `${String(overrideDialog.blockingFlagCount)} unresolved high-severity flag${overrideDialog.blockingFlagCount === 1 ? "" : "s"} ${overrideDialog.blockingFlagCount === 1 ? "is" : "are"} blocking publish.`
                : "One or more unresolved high-severity flags are blocking publish."}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Judges are advisory — you can publish anyway. The override is recorded in the activity
              log and the affected flags are auto-resolved with a note.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOverrideDialog(null)}
            data-testid="content-item-detail-publish-override-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmOverride}
            data-testid="content-item-detail-publish-override-confirm"
          >
            Publish anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/**
 * Experiment versions slot (issue #243). Lists every EXPERIMENT-status
 * ContentVersion newest-first. Each row exposes Promote / Archive /
 * Create-draft actions, mirroring the PageDetailPage analog. Hidden
 * when the item has no EXPERIMENT versions.
 */
function ExperimentVersionsSlot({
  contentItemId,
  experimentVersions,
  onDraftCreated,
  primaryHostname,
}: {
  contentItemId: string;
  experimentVersions: ReadonlyArray<ContentVersionResponse>;
  onDraftCreated: () => void;
  /**
   * Primary public hostname for this site, if any. When present each
   * row exposes a Preview affordance pointing at
   * `/preview/content-version/:versionId` (issue #251) so the operator
   * can render the EXPERIMENT block in isolation without bucketing
   * into a RUNNING experiment. The preview route is auth-gated and
   * accepts any contentVersionId regardless of status — see
   * `findContentVersionById` in `renderer.repository.ts` which
   * intentionally bypasses status filtering for the preview surface.
   */
  primaryHostname: string | undefined;
}): ReactElement | null {
  const toast = useToast();
  const promote = usePromoteContentVersion(contentItemId);
  const archiveExperiment = useArchiveExperimentContentVersion(contentItemId);
  const draftFrom = useDraftFromContentVersion(contentItemId);
  const preview = useContentVersionPreviewHandler(primaryHostname);
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();

  if (experimentVersions.length === 0) return null;

  const handlePromote = async (version: ContentVersionResponse) => {
    const ok = await confirm({
      title: `Promote v${String(version.versionNumber)} to primary?`,
      body: `Promote v${String(version.versionNumber)} to primary? The current primary will be archived.`,
      confirmLabel: "Promote",
      confirmColor: "primary",
    });
    if (!ok) return;
    promote.mutate(
      { versionId: version.id },
      {
        onSuccess: () => toast.showSuccess("Version promoted to primary."),
        onError: (err: ApiClientError) => {
          if (err.code === "VERSION_LOCKED_BY_EXPERIMENT") {
            toast.show({
              message: "This version is locked by a running experiment. Stop the experiment first.",
              severity: "warning",
            });
            return;
          }
          toast.showError(err.message || "Could not promote version.");
        },
      },
    );
  };

  const handleArchiveExperiment = async (version: ContentVersionResponse) => {
    const ok = await confirm({
      title: `Archive experiment v${String(version.versionNumber)}?`,
      body: `Archive experiment v${String(version.versionNumber)}? It will no longer be available as a variant target.`,
      confirmLabel: "Archive",
    });
    if (!ok) return;
    archiveExperiment.mutate(
      { versionId: version.id },
      {
        onSuccess: () => toast.showSuccess("Experiment version archived."),
        onError: (err: ApiClientError) => {
          if (err.code === "VERSION_LOCKED_BY_EXPERIMENT") {
            toast.show({
              message: "This version is locked by a running experiment. Stop the experiment first.",
              severity: "warning",
            });
            return;
          }
          toast.showError(err.message || "Could not archive experiment version.");
        },
      },
    );
  };

  const handleDraftFrom = (version: ContentVersionResponse) => {
    draftFrom.mutate(
      { sourceVersionId: version.id },
      {
        onSuccess: () => {
          toast.showSuccess(`Draft seeded from experiment v${String(version.versionNumber)}.`);
          onDraftCreated();
        },
        onError: (err) => toast.showError(err.message || "Could not create draft."),
      },
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 3 } }}
      data-testid="content-item-detail-experiment-versions-slot"
    >
      {confirmDialog}
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h3">Experiment versions</Typography>
          <Typography variant="body2" color="text.secondary">
            {experimentVersions.length} version(s)
          </Typography>
        </Stack>
        <Stack spacing={1} divider={<Divider flexItem />}>
          {experimentVersions.map((version) => (
            <Stack
              key={version.id}
              spacing={0.5}
              sx={{ py: 1 }}
              data-testid="content-item-detail-experiment-row"
              data-version-id={version.id}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={statusChipColor(version.status)}
                    label={version.status}
                    variant="outlined"
                  />
                  <Typography variant="body2">v{version.versionNumber}</Typography>
                  {version.publishedAt ? (
                    <Typography variant="caption" color="text.secondary">
                      Published {formatDateTime(version.publishedAt)}
                    </Typography>
                  ) : null}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  {primaryHostname ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon fontSize="small" />}
                      onClick={() => preview.openPreview(version.id)}
                      disabled={preview.isPending}
                      data-testid="content-item-detail-experiment-preview-button"
                    >
                      Preview
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDraftFrom(version)}
                    disabled={draftFrom.isPending}
                    data-testid="content-item-detail-experiment-draft-from-button"
                  >
                    Create draft
                  </Button>
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => {
                      void handleArchiveExperiment(version);
                    }}
                    disabled={archiveExperiment.isPending}
                    data-testid="content-item-detail-experiment-archive-button"
                  >
                    Archive
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      void handlePromote(version);
                    }}
                    disabled={promote.isPending}
                    data-testid="content-item-detail-experiment-promote-button"
                  >
                    Promote to primary
                  </Button>
                </Stack>
              </Stack>
              {/* Issue #255 AC1 — backlinks render under the status chip. */}
              <ExperimentBacklinks
                refs={version.referencingExperiments ?? []}
                testIdPrefix="content-item-detail-experiment-row-backlink"
              />
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Recovery card shown in place of the draft slot when no draft exists.
 * Mirrors `NoDraftRecoveryCard` from `PageDetailPage` but adapted to
 * the ContentItem surface:
 *
 *   - If an archived version exists, expose a one-click
 *     `draft-from(ARCHIVED)` CTA so the operator can resume editing.
 *   - If no archived version exists but a published version does, the
 *     PublishedSlot's "Create new draft from this" CTA covers it.
 *   - If neither exists (fresh item, or one whose only draft was
 *     publish-archived without a replacement), offer a "Start a new
 *     draft" CTA that creates an empty `{ schemaVersion: 1 }` version.
 */
function NoDraftRecoveryCard({
  contentItemId,
  recoveryTarget,
  hasPublished,
  onDraftCreated,
}: {
  contentItemId: string;
  recoveryTarget: ContentVersionResponse | null;
  hasPublished: boolean;
  onDraftCreated: () => void;
}): ReactElement {
  const toast = useToast();
  const draftFrom = useDraftFromContentVersion(contentItemId);
  const createVersion = useCreateContentVersion(contentItemId);

  const handleRestore = () => {
    if (!recoveryTarget) return;
    draftFrom.mutate(
      { sourceVersionId: recoveryTarget.id },
      {
        onSuccess: () => {
          toast.showSuccess(`Draft restored from v${String(recoveryTarget.versionNumber)}.`);
          onDraftCreated();
        },
        onError: (err) => toast.showError(err.message || "Could not restore draft."),
      },
    );
  };

  const handleStartEmpty = () => {
    createVersion.mutate(
      { contentJson: { schemaVersion: 1 } },
      {
        onSuccess: () => {
          toast.showSuccess("Empty draft created.");
          onDraftCreated();
        },
        onError: (err) => toast.showError(err.message || "Could not create draft."),
      },
    );
  };

  if (recoveryTarget) {
    const sourceLabel =
      recoveryTarget.status === "ARCHIVED"
        ? `archived v${String(recoveryTarget.versionNumber)}`
        : `v${String(recoveryTarget.versionNumber)}`;
    return (
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, sm: 3 } }}
        data-testid="content-item-detail-no-draft-recovery"
      >
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="h3">No active draft</Typography>
            <Typography variant="body2" color="text.secondary">
              This content item has no draft to edit. Restore the most recent {sourceLabel} as a new
              draft to keep iterating — the source payload is copied into a fresh DRAFT row, leaving
              the source untouched.
            </Typography>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleRestore}
              disabled={draftFrom.isPending}
              data-testid="content-item-detail-restore-archived-button"
            >
              {draftFrom.isPending ? "Restoring…" : `Restore ${sourceLabel} as draft`}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  if (hasPublished) {
    return (
      <Alert severity="info" data-testid="content-item-detail-no-draft">
        No draft version. Use &quot;Create new draft from this&quot; on the published version above
        to start editing.
      </Alert>
    );
  }

  // Fresh item — no draft, no published, no archived. Offer an empty
  // starter so the operator isn't stuck on a blank surface.
  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 3 } }}
      data-testid="content-item-detail-no-draft-empty"
    >
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="h3">No versions yet</Typography>
          <Typography variant="body2" color="text.secondary">
            This content item has no versions yet. Start a blank draft — you can edit its structure
            in the JSON textarea, then publish.
          </Typography>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleStartEmpty}
            disabled={createVersion.isPending}
            data-testid="content-item-detail-start-empty-draft-button"
          >
            {createVersion.isPending ? "Creating…" : "Start a new draft"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Archived slot — collapsible list of ARCHIVED versions, each with
 * Rollback and Preview buttons. Mirrors PageDetailPage's ArchivedSlot
 * but operates on ContentVersions.
 */
function ArchivedSlot({
  contentItemId,
  archived,
  primaryHostname,
  onDraftCreated,
  defaultOpen = false,
}: {
  contentItemId: string;
  archived: ReadonlyArray<ContentVersionResponse>;
  primaryHostname: string | undefined;
  onDraftCreated: () => void;
  defaultOpen?: boolean;
}): ReactElement {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);
  const toast = useToast();
  const draftFrom = useDraftFromContentVersion(contentItemId);
  const preview = useContentVersionPreviewHandler(primaryHostname);
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();

  const handleRollback = async (version: ContentVersionResponse) => {
    const ok = await confirm({
      title: `Roll back to v${String(version.versionNumber)}?`,
      body: `Roll back to v${String(version.versionNumber)}? A new draft will be created from this archived payload.`,
      confirmLabel: "Roll back",
      confirmColor: "warning",
    });
    if (!ok) return;
    draftFrom.mutate(
      { sourceVersionId: version.id },
      {
        onSuccess: () => {
          toast.showSuccess(`Draft seeded from v${String(version.versionNumber)}.`);
          onDraftCreated();
        },
        onError: (err) => toast.showError(err.message || "Could not roll back."),
      },
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 3 } }}
      data-testid="content-item-detail-archived-slot"
    >
      {confirmDialog}
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h3">Archived versions</Typography>
            <Typography variant="body2" color="text.secondary">
              {archived.length === 0 ? "None yet" : `${String(archived.length)} version(s)`}
            </Typography>
          </Stack>
          <IconButton
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Collapse archived versions" : "Expand archived versions"}
            data-testid="content-item-detail-archived-toggle"
            disabled={archived.length === 0}
          >
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
        <Collapse in={open} unmountOnExit>
          <Stack spacing={1} divider={<Divider flexItem />}>
            {archived.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid="content-item-detail-archived-empty"
              >
                No archived versions yet. Publishing a new draft archives the previously published
                one.
              </Typography>
            ) : (
              archived.map((version, archivedIndex) => {
                const previewUrl = buildPreviewUrl(primaryHostname, version.id);
                return (
                  <Stack
                    key={version.id}
                    spacing={1}
                    sx={{ py: 1 }}
                    data-testid="content-item-detail-archived-row"
                    data-version-id={version.id}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Chip
                        size="small"
                        color={statusChipColor(version.status)}
                        label={version.status}
                        variant="outlined"
                      />
                      <Typography variant="body2">v{version.versionNumber}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created {formatDateTime(version.createdAt)}
                      </Typography>
                      {version.archivedAt ? (
                        <Typography variant="caption" color="text.secondary">
                          Archived {formatDateTime(version.archivedAt)}
                        </Typography>
                      ) : null}
                    </Stack>
                    {/* Issue #217: per-row judge strip below the version's
                        metadata, above the per-row action buttons. The
                        Re-run judges button sits beside the strip per row
                        so the operator can refresh an archived version's
                        scores in place. The testid is indexed so QA can
                        target a specific archived row when multiple
                        coexist. */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <JudgeScoresStrip judges={version.judges} />
                      <RerunJudgesButton
                        contentItemId={contentItemId}
                        contentVersionId={version.id}
                        testIdSuffix={`archived-${String(archivedIndex)}`}
                      />
                    </Stack>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                    >
                      {previewUrl ? (
                        <Tooltip title="Preview this archived version">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<OpenInNewIcon fontSize="small" />}
                            onClick={() => preview.openPreview(version.id)}
                            disabled={preview.isPending}
                            data-testid="content-item-detail-archived-preview-button"
                          >
                            Preview
                          </Button>
                        </Tooltip>
                      ) : null}
                      <Button
                        size="small"
                        onClick={() => {
                          void handleRollback(version);
                        }}
                        disabled={draftFrom.isPending}
                        data-testid="content-item-detail-archived-rollback-button"
                      >
                        Rollback
                      </Button>
                    </Stack>
                  </Stack>
                );
              })
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}

/**
 * Top section: ContentItem metadata. Read-only for slice-1; rename is
 * planned but the existing CRUD surface doesn't expose a PATCH on
 * `ContentItem.name` yet — the API would need a `PATCH
 * /api/admin/content-items/:id` route. Marked as a TODO inline so a
 * follow-up issue can add it without losing the design.
 */
function ItemHeader({
  item,
  siteId,
  siteName,
}: {
  item: ContentItemResponse;
  siteId: string;
  siteName: string;
}): ReactElement {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        <Link to="/sites">Sites</Link> /{" "}
        <Link to="/sites/$siteId" params={{ siteId }}>
          {siteName}
        </Link>{" "}
        /{" "}
        <Link to="/sites/$siteId/content" params={{ siteId }}>
          Content
        </Link>{" "}
        / {item.name}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mt: 1 }}
      >
        <Typography variant="h2" data-testid="content-item-detail-name">
          {item.name}
        </Typography>
        <Chip
          size="small"
          label={item.type}
          variant="outlined"
          data-testid="content-item-detail-type-chip"
        />
        {item.blockType ? (
          <Chip
            size="small"
            label={item.blockType}
            variant="outlined"
            data-testid="content-item-detail-block-type-chip"
          />
        ) : null}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5, display: "block" }}
        data-testid="content-item-detail-id"
      >
        {item.id}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        Created {formatDateTime(item.createdAt)} · Updated {formatDateTime(item.updatedAt)}
      </Typography>
    </Box>
  );
}

export function ContentItemDetailPage(): ReactElement {
  const { siteId, contentItemId } = useParams({
    from: "/authenticated/sites/$siteId/content/$contentItemId",
  });

  const site = useSite(siteId);
  const itemQuery = useContentItem(contentItemId);
  const domains = useDomains(siteId);
  const primaryDomain = findPrimaryDomain(domains.data);

  // Draft textarea text lives on the page so the recovery card can
  // hand off control after creating a new draft.
  const [draftText, setDraftText] = useState(EMPTY_CONTENT_JSON_TEXT);

  if (itemQuery.isPending) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}
        data-testid="content-item-detail-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (itemQuery.isError) {
    return (
      <Alert severity="error" data-testid="content-item-detail-error">
        {itemQuery.error.message}
      </Alert>
    );
  }

  if (!itemQuery.data) {
    return (
      <Alert severity="warning" data-testid="content-item-detail-missing">
        Content item not found.
      </Alert>
    );
  }

  const detail: ContentItemWithVersionsResponse = itemQuery.data;
  const { item, latestDraft, published, experimentVersions, archived } = detail;
  const primaryHostname = primaryDomain?.hostname;

  // Recovery target: most-recent archived row when no draft exists.
  const recoveryTarget: ContentVersionResponse | null = latestDraft ? null : (archived[0] ?? null);
  const isDeadEnd = !latestDraft && !published && archived.length > 0;

  const refetch = () => {
    void itemQuery.refetch();
  };

  return (
    <Stack spacing={3} data-testid="content-item-detail-page">
      <ItemHeader item={item} siteId={siteId} siteName={site.data?.name ?? siteId} />

      {/*
       * Slot order matches PR #203's PageDetailPage:
       *   1. Published (with Preview + Unpublish + Create-new-draft)
       *   2. Draft (with Preview + Save + Publish) OR recovery card
       *   3. Archived (collapsible, with per-row Rollback + Preview)
       */}
      {published ? (
        <PublishedSlot
          contentItemId={contentItemId}
          published={published}
          primaryHostname={primaryHostname}
          onDraftCreated={refetch}
        />
      ) : (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, sm: 3 } }}
          data-testid="content-item-detail-no-published-slot"
        >
          <Stack spacing={1}>
            <Typography variant="h3">Currently published</Typography>
            <Typography variant="body2" color="text.secondary">
              {archived.length > 0
                ? "This content item has no live version. Restore an archived version as a draft below, then publish it to bring the block back online."
                : "This content item has no published version yet. Publish a draft below to make it visible on the public site."}
            </Typography>
          </Stack>
        </Paper>
      )}

      {latestDraft ? (
        <DraftSlot
          contentItemId={contentItemId}
          draft={latestDraft}
          primaryHostname={primaryHostname}
          text={draftText}
          setText={setDraftText}
          onVersionRace={refetch}
        />
      ) : (
        <NoDraftRecoveryCard
          contentItemId={contentItemId}
          recoveryTarget={recoveryTarget}
          hasPublished={Boolean(published)}
          onDraftCreated={refetch}
        />
      )}

      {/* Issue #243: experiment versions sit between Draft and Archive
          so operators reading the lifecycle row top→bottom see
          Primary → Draft → Experiments → Archive. The slot is
          self-empty-state-aware (renders nothing when there are no
          EXPERIMENT versions). */}
      <ExperimentVersionsSlot
        contentItemId={contentItemId}
        experimentVersions={experimentVersions}
        onDraftCreated={refetch}
        primaryHostname={primaryHostname}
      />

      <ArchivedSlot
        contentItemId={contentItemId}
        archived={archived}
        primaryHostname={primaryHostname}
        onDraftCreated={refetch}
        defaultOpen={isDeadEnd}
      />
    </Stack>
  );
}
